import { supabase } from '../lib/supabase';

/**
 * Terms and Privacy Policy Service
 * Handles acceptance tracking for terms of service and privacy policy
 */

/**
 * Check if the current user has accepted the terms and privacy policy
 * @returns {Promise<{accepted: boolean, acceptedAt: string|null, error: Error|null}>}
 */
export const hasAcceptedTerms = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { accepted: false, acceptedAt: null, error: new Error('No authenticated user') };
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('terms_accepted_at')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error checking terms acceptance:', error);
      return { accepted: false, acceptedAt: null, error };
    }

    return { 
      accepted: !!data.terms_accepted_at, 
      acceptedAt: data.terms_accepted_at,
      error: null 
    };
  } catch (error) {
    console.error('Error in hasAcceptedTerms:', error);
    return { accepted: false, acceptedAt: null, error };
  }
};

/**
 * Record that the current user has accepted the terms and privacy policy
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export const acceptTermsAndPrivacy = async () => {
  try {
    const { error } = await supabase.rpc('accept_terms_and_privacy');

    if (error) {
      console.error('Error accepting terms:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in acceptTermsAndPrivacy:', error);
    return { success: false, error };
  }
};

/**
 * Get the acceptance status for a specific user (admin only)
 * @param {string} userId - The user ID to check
 * @returns {Promise<{accepted: boolean, acceptedAt: string|null, error: Error|null}>}
 */
export const getUserTermsAcceptance = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('terms_accepted_at')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error checking user terms acceptance:', error);
      return { accepted: false, acceptedAt: null, error };
    }

    return { 
      accepted: !!data.terms_accepted_at, 
      acceptedAt: data.terms_accepted_at,
      error: null 
    };
  } catch (error) {
    console.error('Error in getUserTermsAcceptance:', error);
    return { accepted: false, acceptedAt: null, error };
  }
};
